import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
    AutoComplete,
    Button,
    Form,
    Layout,
    Modal,
    message,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
} from 'antd';
import { FC, useEffect, useState } from 'react';

import {
    FacilityDto,
    FeatureFlagDto,
    FeatureFlagSource,
    FeatureFlagUpdateDto,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import { FEATURE_FLAGS } from '@clh/util';

import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';

export const FeatureFlags: FC = () => {
    const api = useApiClient();
    const [featureFlags, setFeatureFlags] = useState<FeatureFlagDto[]>([]);
    const [facilities, setFacilities] = useState<FacilityDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingFlag, setEditingFlag] = useState<FeatureFlagDto | null>(null);
    const [togglingKey, setTogglingKey] = useState<string | null>(null);
    const [form] = Form.useForm();

    const fetchFeatureFlags = async () => {
        if (!api) return;
        try {
            setLoading(true);
            const response = await api.featureFlagControllerGetFeatureFlags();
            setFeatureFlags(response);
        } catch (_error) {
            message.error('Failed to fetch feature flags');
        } finally {
            setLoading(false);
        }
    };

    const fetchFacilities = async () => {
        if (!api) return;
        try {
            const response = await api.facilityControllerGetFacilities();
            setFacilities(response);
        } catch (_error) {
            message.error('Failed to fetch feature flags');
        }
    };

    useEffect(() => {
        void fetchFeatureFlags();
        void fetchFacilities();
    }, [api]);

    const handleAddFlag = () => {
        setEditingFlag(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditFlag = (flag: FeatureFlagDto) => {
        setEditingFlag(flag);
        form.setFieldsValue({
            key: flag.key,
            isActiveForAll: flag.isActiveForAll,
            activeFacilityIds: flag.activeFacilities.map((f) => f.id),
        });
        setIsModalVisible(true);
    };

    const handleDeleteFlag = async (key: string) => {
        try {
            await api?.featureFlagControllerDeleteFeatureFlag({ flagKey: key });
            setFeatureFlags((flags) =>
                flags.filter(
                    (f) =>
                        f.key !== key || f.source !== FeatureFlagSource.Database
                )
            );
            message.success('Feature flag deleted successfully');
        } catch (_error) {
            message.error('Failed to delete feature flag');
        }
    };

    const handleToggleActiveForAll = async (
        flag: FeatureFlagDto,
        isActiveForAll: boolean
    ) => {
        try {
            setTogglingKey(flag.key);
            const updated = await api?.featureFlagControllerUpdateFeatureFlag({
                featureFlagUpdateDto: {
                    key: flag.key,
                    isActiveForAll,
                    activeFacilityIds: flag.activeFacilities.map((f) => f.id),
                },
            });
            if (updated) {
                setFeatureFlags((flags) =>
                    flags.map((f) =>
                        f.key === updated.key && f.source === updated.source
                            ? updated
                            : f
                    )
                );
            }
        } catch (_error) {
            message.error('Failed to update feature flag');
        } finally {
            setTogglingKey(null);
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const updateDto: FeatureFlagUpdateDto = {
                key: values.key,
                isActiveForAll: values.isActiveForAll,
                activeFacilityIds: values.activeFacilityIds?.map(String) || [],
            };

            if (editingFlag) {
                // Update existing flag
                await api?.featureFlagControllerUpdateFeatureFlag({
                    featureFlagUpdateDto: updateDto,
                });
                message.success('Feature flag updated successfully');
            } else {
                // Create new flag
                await api?.featureFlagControllerUpdateFeatureFlag({
                    featureFlagUpdateDto: updateDto,
                });
                message.success('Feature flag created successfully');
            }

            setIsModalVisible(false);
            void fetchFeatureFlags();
        } catch (_error) {
            message.error('Failed to save feature flag');
        }
    };

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
        },
        {
            title: 'Active For All',
            dataIndex: 'isActiveForAll',
            key: 'isActiveForAll',
            render: (
                isActiveForAll: boolean | null,
                record: FeatureFlagDto
            ) => (
                <Switch
                    checked={isActiveForAll || false}
                    disabled={record.source === FeatureFlagSource.Config}
                    loading={
                        record.source === FeatureFlagSource.Database &&
                        togglingKey === record.key
                    }
                    onChange={(checked) =>
                        void handleToggleActiveForAll(record, checked)
                    }
                />
            ),
        },
        {
            title: 'Active Facilities',
            dataIndex: 'activeFacilities',
            key: 'activeFacilities',
            render: (facilities: { id: string; name: string }[]) =>
                facilities.length > 0
                    ? facilities.map((f) => f.name).join(', ')
                    : 'None',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FeatureFlagDto) =>
                record.source === FeatureFlagSource.Config ? (
                    <Tooltip title="This flag is set in the app/api/public/feature_flags parameter in AWS Systems Manager, so it can't be edited or deleted here. Change it in AWS, then restart the API.">
                        <Tag>Set in AWS</Tag>
                    </Tooltip>
                ) : (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEditFlag(record)}
                        />
                        <Popconfirm
                            title="Delete Feature Flag"
                            description={`Are you sure you want to delete the feature flag "${record.key}"?`}
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDeleteFlag(record.key)}
                        >
                            <Button icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    </Space>
                ),
        },
    ];

    if (!api || loading) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Feature Flags</h2>
            </Header>
            <Content>
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddFlag}
                    >
                        Add Feature Flag
                    </Button>
                </div>

                <Table
                    dataSource={featureFlags}
                    columns={columns}
                    rowKey={(flag) => `${flag.source}:${flag.key}`}
                />

                <Modal
                    title={
                        editingFlag ? 'Edit Feature Flag' : 'Add Feature Flag'
                    }
                    open={isModalVisible}
                    onOk={() => void handleModalOk()}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingFlag ? 'Update' : 'Create'}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="key"
                            label="Key"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a key',
                                },
                            ]}
                        >
                            <AutoComplete
                                placeholder="Select or type a flag key"
                                options={FEATURE_FLAGS.map((flag) => ({
                                    value: flag,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.value ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                        <Form.Item
                            name="isActiveForAll"
                            label="Active For All"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="activeFacilityIds"
                            label="Active Facilities"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select facilities"
                                style={{ width: '100%' }}
                                options={facilities.map((f) => ({
                                    label: f.name,
                                    value: f.id,
                                }))}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
};
